import { createOrganization, getListOrganizationsQueryKey, listOrganizations } from '@np-forge/api-client/organizations'
import type { OrganizationsForUserResponse } from '@np-forge/api-client/types'
import { CancelledError } from '@tanstack/react-query'
/**
 * AuthGuard — route-level orchestrator for the auth/onboarding/protected modes.
 *
 * The decision logic for "where should we send the user after login" lives in
 * ./redirectDecision.ts as pure functions. This file is intentionally thin —
 * it does I/O (listOrganizations, createOrganization), holds React state, and
 * dispatches over the destination union. See redirectDecision.ts for the
 * canonical user flows this code must preserve.
 */
import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { queryClient } from '@/api/client'
import { ErrorScreen } from '@/components/screens/ErrorScreen'
import { LoadingScreen } from '@/components/screens/LoadingScreen'
import { OrganizationNoAccessScreen } from '@/components/screens/OrganizationNoAccessScreen'
import { CURRENT_TOS_VERSION } from '@/constants/tos'
import { useAuth } from '@/context/AuthContext'
import { logger } from '@/lib/logger'
import { withRetry } from '@/lib/retry'
import { isB2bPortalPath, ROUTES } from '@/routes/paths'
import { latestVisitedOrganization } from '@/services/latestVisitedOrganization.service'
import { persistedRedirectUrl } from '@/services/redirectState.service'

import {
  classifySavedRedirect,
  decideProtectedAccess,
  resolvePostAuthDestination,
  type PostAuthDestination,
  type ProtectedAccessDecision,
} from './redirectDecision'

type AuthGuardMode = { type: 'public' } | { type: 'onboarding' } | { type: 'protected' }

interface AuthGuardProps {
  mode: AuthGuardMode
}

/**
 * Public-route resolution state machine.
 *
 * `'idle'` and `'resolving'` both render <LoadingScreen />, but they encode
 * meaningfully different things ("not in public mode / no user yet" vs. "I/O
 * in flight"). Keeping them distinct also keeps the public effect's reset path
 * (no user) honest — it doesn't have to pretend to be resolving.
 */
type PublicResolution =
  | { status: 'idle' }
  | { status: 'resolving' }
  | { status: 'navigate'; to: string }
  | { status: 'bootstrap-failed'; error: Error }

/**
 * Fetch the user's organizations via React Query's cache.
 *
 * We use queryClient.fetchQuery (not the raw listOrganizations function) so that:
 *   - in-flight calls dedupe across components by queryKey,
 *   - the AbortSignal propagates to fetch when the caller's effect cleanup runs,
 *   - retries + abort signals come for free.
 *
 * staleTime is 0 by design: the protected-route flow makes a membership decision
 * on every navigation, and a user invited mid-session must not be stuck on
 * no-access for up to 5 minutes because of a stale cache. In-flight dedup is
 * independent of staleTime, so the public→protected back-to-back call right
 * after login is still de-duplicated when concurrent.
 *
 * Retry config mirrors `withRetry`'s defaults (frontend/src/lib/retry.ts):
 * 3 total attempts, 500/1000/2000 ms exponential backoff.
 *
 * The retry-on-cancellation loop guards against a *foreign* cancellation of the
 * shared organizations query — an abort our own caller never requested. Two real
 * sources:
 *   - React Query aborts the query when its LAST observer unmounts (the Sidebar
 *     hiding during the simulation→feedback transition); a garbage-collected query
 *     has no cached data, so this surfaces as a CancelledError.
 *   - A second AuthGuard effect pass (StrictMode double-invoke in dev, or a fast
 *     re-render) dedupes onto the first pass's in-flight fetch; the first pass's
 *     cleanup then aborts it, surfacing in the second pass as a DOMException
 *     AbortError ("signal is aborted without reason") even though the second
 *     pass's own signal is fine.
 * Neither is a failure: re-issue the fetch (the aborting observer/pass is gone, so
 * the retry runs to completion). A genuine caller abort (signal.aborted) and real
 * errors still propagate, preserving the guard's fail-closed behavior.
 */
function isCancellationError(err: unknown): boolean {
  // A foreign abort takes two shapes: React Query's CancelledError, or an aborted
  // fetch's 'AbortError'. The latter is a DOMException, which does NOT reliably
  // extend Error (jsdom's does not), so match DOMException explicitly as well.
  if (err instanceof CancelledError) return true
  if (err instanceof DOMException && err.name === 'AbortError') return true
  return err instanceof Error && err.name === 'AbortError'
}

const MAX_FOREIGN_CANCEL_RETRIES = 3

async function fetchOrganizations(signal?: AbortSignal): Promise<OrganizationsForUserResponse> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await queryClient.fetchQuery({
        queryKey: getListOrganizationsQueryKey(),
        queryFn: ({ signal: querySignal }) => listOrganizations({ signal: signal ?? querySignal }),
        staleTime: 0,
        retry: 2,
        retryDelay: failureCount => 500 * 2 ** failureCount,
      })
    } catch (err) {
      if (signal?.aborted) throw err
      if (isCancellationError(err) && attempt < MAX_FOREIGN_CANCEL_RETRIES) continue
      throw err
    }
  }
}

/**
 * Resolve where to send an authenticated, ToS-accepted user landing on a public route.
 * Performs I/O; pure logic is delegated to redirectDecision.ts.
 */
async function resolvePublicDestination(
  hasB2bWorkspaceOrInvitation: boolean,
  signal?: AbortSignal,
): Promise<PostAuthDestination> {
  let workspaces: OrganizationsForUserResponse
  try {
    workspaces = await fetchOrganizations(signal)
  } catch (err) {
    // A foreign cancellation is absorbed (retried) inside fetchOrganizations, so
    // this only fires for a real listOrganizations failure during public
    // resolution — fall back to ToS rather than guessing a workspace.
    logger.warn('listOrganizations failed during public-route resolution:', err)
    return { kind: 'tos' }
  }

  const classification = classifySavedRedirect(persistedRedirectUrl.get(), workspaces)
  return resolvePostAuthDestination(
    classification,
    latestVisitedOrganization.get(),
    workspaces,
    hasB2bWorkspaceOrInvitation,
  )
}

/**
 * Run the side effects for a destination and return the URL to navigate to
 * (or `null` if the destination renders a screen rather than navigating).
 */
async function executeDestination(destination: PostAuthDestination, userEmail: string): Promise<string | null> {
  switch (destination.kind) {
    case 'go-to-saved':
      // Saved redirect stays in storage; the protected guard will clear it on
      // successful render. Keeping it here simplifies the no-access path,
      // where we want to clear it explicitly.
      return destination.path

    case 'accept-invitation':
      // Saved redirect is intentionally NOT cleared — OrganizationInvite reads
      // it and navigates back on accept.
      return ROUTES.ORGANIZATION_INVITE.buildPath({ organizationId: destination.organizationId })

    case 'clone-workspace':
      persistedRedirectUrl.clear()
      return destination.path

    case 'invite-link':
      // Return to /invite so the page redeems the token still in sessionStorage.
      // Clear the saved redirect now (we're consuming it); the page sets a fresh
      // destination based on the redeem response.
      persistedRedirectUrl.clear()
      return destination.path

    case 'b2b-page':
      // Flow E: we're consuming the saved B2B link now, as with clone-workspace.
      persistedRedirectUrl.clear()
      return destination.path

    case 'go-to-saved-no-access':
      persistedRedirectUrl.clear()
      return destination.path

    case 'workspace-library':
      return ROUTES.WORKSPACE_LIBRARY.buildPath({ id: destination.organizationId })

    case 'b2b-home':
      // Flow B3. Like workspace-library, it clears nothing: the protected guard does when /work renders.
      return ROUTES.B2B.HOME.fullPath

    case 'create-default-workspace': {
      const created = await createDefaultWorkspace(userEmail)
      // Seed the listOrganizations cache so the protected guard for the new
      // workspace doesn't cache-hit the empty list we just populated and
      // bounce the brand-new user to <OrganizationNoAccessScreen />.
      queryClient.setQueryData<OrganizationsForUserResponse>(getListOrganizationsQueryKey(), previous => ({
        organizations: [...(previous?.organizations ?? []), created],
        invitations: previous?.invitations ?? [],
      }))
      return ROUTES.WORKSPACE_LIBRARY.buildPath({ id: created.id })
    }

    case 'tos':
      return ROUTES.ACCEPT_TERMS_OF_SERVICE.fullPath

    case 'bootstrap-failed':
      // Renders the error screen; no navigation.
      return null
  }
}

async function createDefaultWorkspace(email: string) {
  // Wrapped in withRetry so a transient blip during account bootstrap doesn't
  // strand a brand-new user. On terminal failure, the IIFE catch falls back
  // to `bootstrap-failed` and renders ErrorScreen with a reload button.
  return await withRetry(() => createOrganization({ name: `${email} workspace`, default_scenario_aliases: [] }))
}

async function resolveProtectedAccess(currentPath: string, signal?: AbortSignal): Promise<ProtectedAccessDecision> {
  // A B2B portal page needs no original workspace, so it never waits on, or fails with, the workspace list.
  const decision: ProtectedAccessDecision = isB2bPortalPath(currentPath)
    ? { kind: 'allow' }
    : decideProtectedAccess(currentPath, await fetchOrganizations(signal))
  switch (decision.kind) {
    case 'allow': {
      // Record the visit so the next blank-/ entry lands here. decision.organizationId
      // is set ⇔ the user is a member of an org named in the path; absent for catch-all
      // paths like /clone-workspace/X (which also resolve to 'allow' but must not
      // update latest-visited).
      if (decision.organizationId) {
        latestVisitedOrganization.save(decision.organizationId)
      }
      // The user is on their intended page. Drop any leftover saved redirect.
      persistedRedirectUrl.clear()
      break
    }
    case 'invite-required':
      // Flow A3: keep the deep link so OrganizationInvite returns the user
      // back here on accept, instead of dropping them on the generic library.
      persistedRedirectUrl.save(currentPath)
      break
    case 'no-access':
    case 'unknown-error':
      // Leave any prior saved redirect untouched.
      break
  }
  return decision
}

/**
 * The onboarding screen an authenticated user still owes before app access, or
 * null when every prerequisite is met. Order mirrors the backend gate precedence
 * (451 ToS before 432 name): accept ToS first, then have a valid first name.
 */
function pendingOnboardingRoute(tosAccepted: boolean, nameValid: boolean): string | null {
  if (!tosAccepted) return ROUTES.ACCEPT_TERMS_OF_SERVICE.fullPath
  if (!nameValid) return ROUTES.ONBOARDING.fullPath
  return null
}

export default function AuthGuard({ mode }: AuthGuardProps) {
  const { user, loading } = useAuth()
  const routerLocation = useLocation()

  const [publicResolution, setPublicResolution] = useState<PublicResolution>({ status: 'idle' })

  // undefined = "not computed yet"; non-undefined values are real decisions.
  const [protectedDecision, setProtectedDecision] = useState<ProtectedAccessDecision | undefined>(undefined)

  const tosAccepted = user?.tos_version === CURRENT_TOS_VERSION
  // first_name is null on the wire exactly when the stored name is absent or invalid.
  const nameValid = user?.user.first_name != null
  // The onboarding screen still owed (null when none) — drives both the public-route
  // navigate and the protected-route bounce, in the backend's gate order (ToS, name).
  const onboardingRoute = user ? pendingOnboardingRoute(tosAccepted, nameValid) : null

  // Public-route effect: only fires for `mode='public' && user`.
  // Computes destination + executes its side effects (clear redirect, create workspace).
  useEffect(() => {
    if (mode.type !== 'public' || !user) {
      setPublicResolution({ status: 'idle' })
      return
    }

    if (onboardingRoute) {
      // Pre-ToS or invalid-name: send to the matching onboarding screen without
      // calling listOrganizations — the user can't act on a workspace yet.
      setPublicResolution({ status: 'navigate', to: onboardingRoute })
      return
    }

    setPublicResolution({ status: 'resolving' })

    // `cancelled` and `abort` work at different layers and are both needed:
    // `abort` cuts the in-flight fetch at the network so the browser stops
    // wasting bandwidth; `cancelled` guards each `await` so a request that
    // already resolved before cleanup ran can't trigger a setState on an
    // unmounted (or stale) component.
    let cancelled = false
    const abort = new AbortController()
    const userEmail = user.user.email
    const hasB2bWorkspaceOrInvitation = user.has_b2b_workspace_or_invitation
    void (async () => {
      try {
        const destination = await resolvePublicDestination(hasB2bWorkspaceOrInvitation, abort.signal)
        if (cancelled) return
        const navTo = await executeDestination(destination, userEmail)
        if (cancelled) return
        if (navTo === null) {
          // executeDestination returns null only for `bootstrap-failed`; today
          // that path is reached via the catch below. Future destinations that
          // render a screen instead of navigating should also land here.
          setPublicResolution({ status: 'bootstrap-failed', error: new Error('No navigation target') })
          return
        }
        setPublicResolution({ status: 'navigate', to: navTo })
      } catch (err) {
        if (cancelled) return
        // Terminal failure inside executeDestination — most realistically the
        // brand-new-user bootstrap (Flow B1) couldn't create a default workspace.
        // Render an ErrorScreen with a reload button rather than spinning forever.
        // (Foreign cancellations are absorbed inside fetchOrganizations.)
        logger.error("We couldn't finish setting up your workspace. Showing the reload screen.", err)
        const error = err instanceof Error ? err : new Error(String(err))
        setPublicResolution({ status: 'bootstrap-failed', error })
      }
    })()

    return () => {
      cancelled = true
      abort.abort()
    }
  }, [mode.type, user, onboardingRoute])

  // Protected-route effect: only fires for `mode='protected'`.
  useEffect(() => {
    if (mode.type !== 'protected' || loading) {
      setProtectedDecision(undefined)
      return
    }

    const currentPath = routerLocation.pathname + routerLocation.search + routerLocation.hash

    // Unauthenticated or pre-ToS user on a protected URL: save it so we can
    // return after auth/ToS. The renderer below will Navigate to root before
    // it ever checks protectedDecision, so we leave the decision as undefined.
    if (!user || onboardingRoute) {
      persistedRedirectUrl.save(currentPath)
      setProtectedDecision(undefined)
      return
    }

    // Saved-redirect side effects are deferred to resolveProtectedAccess so we
    // can branch on the decision: clear on 'allow', save the path on
    // 'invite-required' (Flow A3), leave alone otherwise.

    let cancelled = false
    const abort = new AbortController()
    void (async () => {
      try {
        const decision = await resolveProtectedAccess(currentPath, abort.signal)
        if (!cancelled) setProtectedDecision(decision)
      } catch (err) {
        if (cancelled) return
        // Fail CLOSED — the previous behaviour was to render the workspace shell
        // when listOrganizations failed, which leaked the shell to non-members
        // when retries exhausted. We render an ErrorScreen with reload instead.
        // Foreign cancellations are absorbed inside fetchOrganizations, so this
        // only fires on a real failure; logger.error reports it to Sentry.
        logger.error("We couldn't verify your workspace access. Showing the reload screen.", err)
        const error = err instanceof Error ? err : new Error(String(err))
        setProtectedDecision({ kind: 'unknown-error', error })
      }
    })()

    return () => {
      cancelled = true
      abort.abort()
    }
    // search/hash intentionally excluded: org membership is decided by pathname,
    // and re-running this effect on every query-string change caused a request
    // storm against /v1/organizations/. The 401-save branch above also only fires
    // for unauthenticated/pre-ToS transitions, where pathname has already changed.
  }, [mode.type, user, onboardingRoute, loading, routerLocation.pathname])

  // Place the loading gate AFTER hooks to keep hook order stable across renders.
  if (loading) return <LoadingScreen />

  switch (mode.type) {
    case 'public':
      return renderPublic({ user, publicResolution })

    case 'onboarding':
      if (!user) return <Navigate to={ROUTES.ROOT.fullPath} replace />
      return <Outlet />

    case 'protected':
      return renderProtected({ user, onboardingRoute, protectedDecision })
  }
}

function renderPublic({
  user,
  publicResolution,
}: {
  user: ReturnType<typeof useAuth>['user']
  publicResolution: PublicResolution
}) {
  if (!user) return <Outlet />
  switch (publicResolution.status) {
    case 'idle':
    case 'resolving':
      return <LoadingScreen />
    case 'navigate':
      return <Navigate to={publicResolution.to} replace />
    case 'bootstrap-failed':
      return (
        <ErrorScreen
          message="We couldn't finish setting up your workspace. Please reload to try again."
          onAction={() => window.location.reload()}
        />
      )
  }
}

function renderProtected({
  user,
  onboardingRoute,
  protectedDecision,
}: {
  user: ReturnType<typeof useAuth>['user']
  onboardingRoute: string | null
  protectedDecision: ProtectedAccessDecision | undefined
}) {
  // Unauthenticated / onboarding-incomplete (pre-ToS or invalid name): send to root
  // (the public guard at / routes onward). The protected effect already saved the path.
  if (!user || onboardingRoute) return <Navigate to={ROUTES.ROOT.fullPath} replace />

  if (protectedDecision === undefined) return <LoadingScreen />

  switch (protectedDecision.kind) {
    case 'allow':
      return <Outlet />
    case 'invite-required':
      return (
        <Navigate
          to={ROUTES.ORGANIZATION_INVITE.buildPath({ organizationId: protectedDecision.organizationId })}
          replace
        />
      )
    case 'no-access':
      return <OrganizationNoAccessScreen />
    case 'unknown-error':
      // We don't know if the user has access — fail closed and let them retry,
      // rather than leak the workspace shell or spin forever.
      return (
        <ErrorScreen
          message="We couldn't verify your workspace access. Please reload to try again."
          onAction={() => window.location.reload()}
        />
      )
  }
}
